import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const reviewVideoSkill: Skill = {
    name: SKILL_NAMES.reviewVideo,
    description:
        'The video review procedure. Load BEFORE starting any review — the review phase of every build, or when asked to review or polish a video.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Reviewing a Video

The review is pass/fail against a closed defect list — not a subjective score — so
it terminates. A defect \`${TOOL_NAMES.reviewSceneImages}\` returns obligates you to fix
it and re-check, never to note it and move on.

## Procedure
1. **Open with one full render**: \`${TOOL_NAMES.reviewSceneImages}\` on every scene, all in
   one call rather than one call per scene, with \`video_subject\`: one line on what the video
   is about, so footage is judged against the video and not only the scene's own copy, and
   \`${TOOL_NAMES.lintVideo}\` in the same turn. This is the only turn of the review that
   asks for a verdict without also making fixes — there is nothing to fix yet.
2. **Each cycle after that is one turn.** Send every fix the review and lint called
   for, then in that same turn \`${TOOL_NAMES.reviewSceneImages}\` on the scenes you
   changed and \`${TOOL_NAMES.lintVideo}\`. Edits always run before the review and lint
   in a turn, so they judge the fixed video — you never have to wait for a fix to land
   before asking for the verdict on it. Fixes on different scenes are independent, so
   there is no reason to send one, wait, then send the next.
3. **Repeat until the review passes and lint reports no errors.** A turn that only
   re-reviews, or only lints, is a wasted round trip: if you have nothing to fix, you
   are done; if you have something to fix, fix it and verify in the same turn.

**Done means:** every scene has passed \`${TOOL_NAMES.reviewSceneImages}\` and
\`${TOOL_NAMES.lintVideo}\` reports zero errors. Lint warnings do not hold the video back.

## What the tool doesn't check
A few things stay yours to judge, because they're about whether the words are the
right words, not about what the frame looks like:
- On a text scene (no narration): does the on-screen text communicate a complete
  thought on its own? (Not required on a voiceover/talking scene — the narration
  carries the thought there.)
- On a voiceover scene: review each overlay separately against the words it
  covers. Its on-screen text should reinforce that line — its key phrase,
  number, or payoff — not transcribe it, and must never contradict it or add
  a claim or figure the narration doesn't make there. Confirm split points
  read naturally too. No check on the closed list expresses overlay/narration
  correspondence or split-point naturalness, so this has no tool-side
  equivalent and stays a manual check every time.
- **Text that was never meant to be user-facing is still a defect** — a stray
  note, an instruction, or copy left at the template's default. Nothing on the
  closed list covers it: on screen it is indistinguishable from the imperative
  copy a marketing video is made of, and in the transcript the reviewer never
  sees it at all. \`${TOOL_NAMES.lintVideo}\` flags untouched template defaults;
  read the transcript yourself for anything else.

**Swap the design instead of degrading the copy.** If making a scene's text fit
has taken more than ~2 rewrites, or the words only fit as disjointed fragments,
the design is wrong for that content. Pick a different, more suitable scene design and
delete/recreate the scene.

**The tool releases a scene on its own after 3 fix rounds:** a scene that keeps
failing, on whatever it fails on, stops blocking and is reported as
accepted-with-residual instead. That is
mechanical, not something you need to track or decide — keep fixing for as long as
the tool keeps blocking, and don't stop early on your own judgment that a defect is
unfixable.

## Reading the lint report
\`${TOOL_NAMES.lintVideo}\` runs alongside every review, not as a step of its own.
- **Errors** must be fixed — all of them, in the next cycle's batch.
- **Warnings** are heuristics. Read them once, fix any that is clearly wrong for this
  video in the same batch as anything else you're changing, and leave the rest. A
  warning-free lint is not the goal, and a warning you chose to leave is not a reason
  for another cycle.

## What lint doesn't check
Verify what lint can't by inspecting the video state:
1. **Narration present?** A voiceover/talking-head/recorded group exists — unless
   the user explicitly opted out.
2. **Every scene built?** No empty or half-built scenes.
3. **Lists and numbers hold up?** Counts match, no dropped items, referenced
   numbers visible.
4. **Text legible and self-standing?** No tiny type, no cryptic fragments
5. **Does it look good?** Opens on an effective intro/title scene; list scenes use list designs;
   quote designs hold real quotations with the speaker credited in the name/role
   slots, not ordinary copy with those slots left empty; media fits and looks authentic.
6. **A complete story?** Read the whole video as a viewer — narration end to end
   plus on-screen text and media in order. It opens with a real hook, flows, and
   closes with a real outro rather than stopping mid-thought.

If any of these fails, fix it — batched with the next cycle's fixes, review and lint.
Once the review is clean and lint reports zero errors, the video is done: finish
rather than running another verification pass.`;
    },
};
